import DeploymentSummary from "../components/DeploymentSummary";
import SnapshotExecution from "../components/SnapshotExecution";
import { CicdMonitorButton, ExecutionErrors, executionOf } from "../../../components/CicdExecution";
// Scene 5: review, execution and navigation to stage approvals.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, AlertTitle, Box, Button, Checkbox, CircularProgress, FormControlLabel, InputAdornment, LinearProgress, Link, TextField } from "@mui/material";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import PartnerDirectoryInfo from "../components/PartnerDirectoryInfo";
import CheckRounded from "@mui/icons-material/CheckRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { usePdEdgeCapabilities, pdEdgeCapabilityStages, usePdEdgeDefaults, edgePlanBlocked, edgeRuntimeChoiceRequired, HiddenEdgeTargets } from "../components/PdEdgeTargets";
import { PdEdgeModeControl, PdEdgeRuntime } from "../components/PdEdgePlan";
import PartnerDirectoryEdgeSync from "../../../components/PartnerDirectoryEdgeSync";
import useDevPdEdgeCapabilities from "../hooks/useDevPdEdgeCapabilities";
import "../ReviewMui.css";
import { useLang } from "../i18n";
import { useTransportV2, baseStageOf, usesWerke, activeWerke, vorwaertsStufen, vorwaertsZiele, vorwaertsAlleZiele, vorwaertsZielKette, pdPidsFuerStufe, deploymentSelection, referenceAutoDeploy, refNutzer, refGebraucht, gewaehlteIflows, pdEdgeSelection, packageStates, pdEdgeMode, pdEdgeConflictPids } from "../context/TransportV2Context";
import { plantTargetPreset } from "../utils/plantTargetPreset";
import { needsSameReferenceVersionPermission, referenceVersions, isArtifactDraft } from "../utils/sameVersion";
import { stageLabel } from "../../../utils/stages";
import useTransportPreflight from "../hooks/useTransportPreflight";
import { transportPreflightError } from "../../../utils/transportPreflightErrors";
import { useCicdRun } from "../hooks/useCicdRun";
import { useStageBranchesConfig } from "../../../stage-branches/useStageBoard";
import { isStageBranchesActive } from "../../../stage-branches/stageBranchesActive";
import { topicRequirement } from "../../../stage-branches/topicRequirement";
import { usePipelineReady } from "../hooks/usePipelineReady";
import { api } from "../../../utils/helpers";
import { buildConfigsByWerk } from "../../../utils/transport-config-helpers";
import { Cx } from "../components/IconSprite";
import TargetName from "../components/TargetName";
import RunLiveCard from "../components/RunLiveCard";
import BatchReview from "../components/BatchReview";
import TopicSelection from "../../../components/TopicSelection";
import useSpaceTopics, { canonicalTopicSpace, selectedTopicId, selectedTopicSelection, topicSelectionPayload } from "../../../hooks/useSpaceTopics";
import ReviewEdgeStatus from "../components/ReviewEdgeStatus";
import PlantChangeConfirmation from "../components/MuiPlantReview";
import ReviewPackageRetirement, { retirementTargets } from "../components/ReviewPackageRetirement";
import { plantChangesConfirmed } from "../utils/plantChanges";
import { transportTargetStage, transportTargetIndex } from "../utils/transportTarget";
import { requestPlantNaming, validPlantNaming, plantCatalogRevision, plantTargetRevisions } from "../utils/plantNaming";
import { plantNamingError } from "../../../utils/transportBatchErrors";
import { isPackageTransport, packageIflowIds } from "../utils/packageTransport";
import { WholePackageReview } from "../components/WholePackageSection";
import PackageInfoAlignment, { hasPackageInfoAlignment } from "../components/PackageInfoAlignment";
import useExtensionVersion from "../hooks/useExtensionVersion";
import { packageTransportText } from "../../../utils/transportPackageErrors";

export { transportTargetStage, transportTargetIndex } from "../utils/transportTarget";

// Zielpaket einer Vorprüfungsmeldung: bei einem Werk der Servername aus dem Vergleich mit
// hervorgehobener Kennung, die technische ID nur als Zusatz. Ohne Werk bleibt es bei der ID.
export function PreflightPackage({ state, packageId }) {
  const entry = Object.values(state.precheckByBucket || {}).find(item => item?.werk?.kennung
    && item.targetPackageId === packageId && typeof item.targetPackageName === "string" && item.targetPackageName);
  if (!entry) return <Box component="strong" sx={{ display: "block" }}>{packageId}</Box>;
  return <>
    <Box component="strong" sx={{ display: "block" }}>
      <TargetName source={state.pkgName || state.pkgId} target={entry.targetPackageName} kennung={entry.werk.kennung} />
    </Box>
    <Box component="span" sx={{ display: "block" }}>{packageId}</Box>
  </>;
}

function selectedIflows(state) {
  const cmp = state.precheckByBucket || {};
  const seen = new Set();
  const list = [];
  for (const entry of Object.values(cmp)) {
    if (!entry || !Array.isArray(entry.comparison)) continue;
    for (const a of entry.comparison) {
      if (a.draft) continue;
      const src = a.originalId || a.Id;
      if (seen.has(src)) continue;
      // hat mind. ein Werk in sel?
      const sel = state.sel?.[src];
      if (!sel) continue;
      const anyOn = Object.keys(sel).some((k) => sel[k]);
      if (!anyOn) continue;
      seen.add(src);
      list.push({ id: src, name: a.Name || src, version: a.Version || "-" });
    }
  }
  return list;
}

// The selected plants for the snapshot: it creates one package folder per kennung in Git.
// Without plants the list stays empty and only the source package is created.
//
// Jedes Werk traegt seine EIGENE Artefakt-Auswahl mit. Ohne sie kennt das Backend nur die
// flache Liste und vervielfacht sie auf alle Werke - die Abwahl einzelner Zeilen in Schritt 3
// war damit wirkungslos. Quelle ist state.sel[sourceId][werkKey]; der werklose Anteil ("std")
// kommt hier nicht vor, weil bei aktiven Werken kein "std"-Bucket existiert.
// Ein Werk ohne angehaktes Artefakt faellt komplett heraus: ein leeres Zielpaket wuerde im
// SAP-Modus das Paket im Ziel loeschen und leer neu anlegen.
export function collectWerke(state, iflows) {
  if (!usesWerke(state)) return [];
  const sel = state.sel || {};
  const quellIds = (iflows || []).map((f) => f.id);
  return activeWerke(state)
    .filter((w) => w.kennung)
    .map((w) => ({
      kennung: w.kennung,
      name: w.name || w.kennung,
      // Der Werk-Schluessel ist die Kennung (Scene3 schreibt sel mit entry.werk.kennung,
      // die Buckets lesen mit w.key) - beide Schreibweisen pruefen, damit ein abweichender
      // Key die Auswahl nicht still leer laufen laesst.
      artifactIds: quellIds.filter((src) => !!(sel[src]?.[w.key] || sel[src]?.[w.kennung])),
    }))
    .filter((w) => w.artifactIds.length > 0);
}

// Ausgewaehlte globale Referenzen fuer den Snapshot (Vertrag: [{ id, type, typ }]). Sie reisen
// im Snapshot mit und werden vom Ziel-Workflow VOR den iFlows hochgeladen und deployt.
//   - Id VOR Name als Schluessel: der Snapshot laedt per Id (Name != Id liefe in einen 404).
//   - type ist das Label (package-references schreibt es GROSS: Type), typ der Schluessel
//     (ScriptCollection | MessageMapping | ValueMapping). Das Backend bevorzugt typ und faellt
//     auf type zurueck - typ geht deshalb nur mit, wenn es da ist.
//   - Eine Referenz, die nicht mehr in globalRefs steht (Liste zwischenzeitlich neu geladen),
//     bleibt draussen: mit leerem Typ fragte der Snapshot den falschen Entity-Set ab.
//   - transportierbar=false (Function Library u. a.) bleibt IMMER draussen, auch wenn refSel
//     die Referenz irgendwie fuehrt: gemeldet ja, transportiert nie.
//   - Pakettransport (Welle 6): Eigene Referenzen liegen im SAP-Export des Pakets, fremde werden nur geprüft. Es geht
//     keine Referenz zusätzlich als globale Referenz mit, sonst reiste sie doppelt.
export function collectGlobalRefs(state) {
  const out = [];
  if (isPackageTransport(state)) return out;
  for (const [k, v] of Object.entries(state.refSel || {})) {
    if (!v) continue;
    const ref = (state.globalRefs || []).find((r) => (r?.bundleId || r?.id || r?.Id || r?.name || r?.Name) === k);
    // DEV183: Fremde Referenzen (checkOnly) werden nur geprüft, nie mitgeschickt.
    if (!ref || ref.checkOnly === true || ref.transportierbar === false || ref.resolvable === false || isArtifactDraft({ ...ref, ...referenceVersions(ref) })
      || !refGebraucht(ref, gewaehlteIflows(state.sel))
      || (needsSameReferenceVersionPermission(ref) && !state.canTransportSameVersionReferences)) continue;
    const typ = ref.typ || ref.Typ || "";
    // DEV183 C1: Herkunft und Paket der Referenz mitsenden, damit der Server eigene Referenzen erkennt (nur wenn bekannt).
    const packageId = ref.packageId || ref.sourcePackageId;
    out.push({ id: k, type: ref.type || ref.Type || "", ...(typ ? { typ } : {}),
      ...(typeof ref.herkunft === "string" && ref.herkunft ? { herkunft: ref.herkunft } : {}),
      ...(typeof packageId === "string" && packageId ? { packageId } : {}) });
  }
  return out;
}

// Pakettransport (W6.5): alle iFlows des Pakets (auch Entwürfe, der Export enthält sie) und jedes gewählte Werk.
// Der Server transportiert ohnehin das ganze Paket; die Liste nennt nur, was im Paket steht.
function wholePackageWerke(state, artifactIds) {
  if (!usesWerke(state)) return [];
  const werke = activeWerke(state).filter(w => w.kennung).map(w => ({ kennung: w.kennung, name: w.name || w.kennung, artifactIds: [...artifactIds] }));
  const targets = state.space?.qasInstance?.id ? [state.space.qasInstance.id] : state.targetInstanceIds || [];
  const missing = !werke.length || werke.some(plant => targets.some(target => {
    const entry = state.precheckByBucket?.[`${target}__${plant.kennung}`];
    return !entry || entry.error || !Array.isArray(entry.comparison) || entry.werk?.kennung !== plant.kennung;
  }));
  if (missing) throw Object.assign(new Error("PLANT_TARGET_PLAN_CHANGED"), { code: "PLANT_TARGET_PLAN_CHANGED" });
  return werke;
}

/** Ein Paket ist startbar mit mindestens einem iFlow oder als ganzes Paket (reines Designpaket ohne iFlow). */
export const packageStartable = pkg => !!pkg && Array.isArray(pkg.artifactIds) && (pkg.artifactIds.length > 0 || pkg.transport === "package");

export function batchPackagePayload(state, sapModus) {
  const whole = isPackageTransport(state);
  const iflows = selectedIflows(state);
  const refs = collectGlobalRefs(state);
  const artifactIds = whole ? packageIflowIds(state) : iflows.map(flow => flow.id);
  const werke = whole ? wholePackageWerke(state, artifactIds) : collectWerke(state, iflows);
  const targetRevisions = plantTargetRevisions(state);
  if (!whole && usesWerke(state) && iflows.length) {
    const selectedPlants = activeWerke(state);
    const allowedKeys = new Set(selectedPlants.flatMap(plant => [plant.key, plant.kennung]));
    const targets = state.space?.qasInstance?.id ? [state.space.qasInstance.id] : state.targetInstanceIds || [];
    const invalid = !werke.length || iflows.some(flow =>
      Object.entries(state.sel?.[flow.id] || {}).some(([key, selected]) => selected && !allowedKeys.has(key))
      || !werke.some(plant => plant.artifactIds.includes(flow.id)))
      || werke.some(plant => targets.some(target => {
        const entry = state.precheckByBucket?.[`${target}__${plant.kennung}`];
        const comparison = entry?.comparison || [];
        const hasChanges = entry?.plantChanges?.length || comparison.some(artifact => artifact.plantChanges?.length);
        return (hasChanges && entry?.werk?.kennung !== plant.kennung)
          || plant.artifactIds.some(id => !comparison.some(artifact => !artifact.draft && (artifact.originalId || artifact.Id) === id));
      }));
    if (invalid) throw Object.assign(new Error("PLANT_TARGET_PLAN_CHANGED"), { code: "PLANT_TARGET_PLAN_CHANGED" });
  }
  return {
    packageId: state.pkgId, packageName: state.pkgName || state.pkgId,
    // Nur beim Pakettransport: reine iFlow-Pakete behalten ihre bisherige Nutzlast byte-gleich.
    ...(whole ? { transport: "package" } : {}),
    artifactIds, werke,
    plantChangeConfirmations: state.plantChangeConfirmations || [],
    plantNaming: requestPlantNaming(state.plantNaming),
    plantCatalogRevision: plantCatalogRevision(state),
    plantTargetRevisions: targetRevisions,
    configsByWerk: buildConfigsByWerk(state, vorwaertsAlleZiele(state), state.gitCfg, vorwaertsZielKette(state)),
    ...(sapModus ? {} : { deployment: deploymentSelection(state) }),
    globalRefs: sapModus ? [] : refs.map(ref => {
      const original = (state.globalRefs || []).find(item => (item.bundleId || item.id || item.Id || item.name || item.Name) === ref.id);
      return { ...ref, deploy: referenceAutoDeploy(state, original), referencedBy: refNutzer(original) };
    }),
    partnerIds: sapModus || !state.pdReplicate ? [] : [...new Set(state.pdSelectedPids || [])],
    pdStagesByPid: sapModus || !state.pdReplicate ? {} : state.pdStagesByPid || {},
    pdEdgeByPid: sapModus ? {} : pdEdgeSelection(state),
    // Nur im Mehrzielmodus: Wizard Auswahl der Ziele je Stufe als Vorbelegung des Antrags (nur Webapp, nicht Git).
    ...(state.multiTarget ? { stageTargets: stageTargetSelection(state) } : {}),
    // Werkskatalog (W4.5): je Werk eigene Ziele, nur bei mindestens zwei Werken mit unterschiedlichen Werten.
    ...(() => { const byWerk = stageTargetsByWerk(state); return byWerk ? { stageTargetsByWerk: byWerk } : {}; })(),
  };
}

/**
 * `{ [kennung]: { [stage]: targetId[] } }` oder `null`. Je Werk gilt der Katalogwert (oder das Standardziel) plus die vom
 * Benutzer hinzugefügten Ziele minus die entfernten, gemessen an der Vorbelegung (Vereinigung der gewählten Werke).
 * Wird eine Stufe dadurch leer, gilt die Endauswahl der Stufe.
 */
export function stageTargetsByWerk(state) {
  if (!state.multiTarget || !state.stageTargets || !state.plantTargets) return null;
  const kennungen = activeWerke(state).map(plant => plant.kennung).filter(Boolean);
  if (kennungen.length < 2) return null;
  const final = stageTargetSelection(state);
  const baseStage = baseStageOf(state.space);
  const union = plantTargetPreset({ stageTargets: state.stageTargets, plantTargets: state.plantTargets, kennungen, baseStage }).selection;
  const out = {};
  for (const kennung of kennungen) {
    const own = plantTargetPreset({ stageTargets: state.stageTargets, plantTargets: state.plantTargets, kennungen: [kennung], baseStage }).selection;
    out[kennung] = {};
    for (const [stage, chosen] of Object.entries(final)) {
      const known = (state.stageTargets[stage] || []).map(row => row.targetId);
      const added = chosen.filter(id => !(union[stage] || []).includes(id));
      const removed = (union[stage] || []).filter(id => !chosen.includes(id));
      const ids = known.filter(id => ((own[stage] || []).includes(id) || added.includes(id)) && !removed.includes(id));
      out[kennung][stage] = ids.length ? ids : [...chosen];
    }
  }
  const values = Object.values(out).map(entry => JSON.stringify(entry));
  return values.every(value => value === values[0]) ? null : out;
}

// Frist der beiden Partner-Directory-Aufrufe (Ermittlung und Liste der Quelle): wie die übrigen langen Transportaufrufe.
// Die 30 s Vorgabe von api() brach die Ermittlung auf großen Tenants ab (Einlege-Fehler 02.11.2026).
export const PD_LOAD_TIMEOUT_MS = 180000;
// Fachlicher Text für ein gescheitertes Laden: der Abbruchtext des Browsers („signal is aborted without reason“)
// erreicht die Maske nicht. Eine Serverablehnung nennt ihren eigenen Grund (in der Sprache der Anfrage).
export function pdLoadError(error, t, prefix) {
  if (error?.name === "AbortError") return t(`${prefix}Timeout`);
  const serverText = typeof error?.body?.error === "string" ? error.body.error.trim() : "";
  return serverText || t(`${prefix}Failed`);
}

// { [stage]: targetId[] } der Stufen ab dem Ziel, in Reihenfolge der Ziele je Stufe.
function stageTargetSelection(state) {
  const stages = new Set(vorwaertsStufen(state).map(stage => stage.key));
  return Object.fromEntries(Object.entries(state.stageTargetSel || {}).filter(([stage]) => stages.has(stage)).map(([stage, ids]) => [stage, [...ids]]));
}

export default function Scene5Review({ user }) {
  const { t, lang } = useLang();
  const { state, dispatch } = useTransportV2();
  const multiPackage = (state.selectedPackages || []).length > 1;
  const transportScope = `${user?.id || ""}:${state.spaceId || ""}:${state.sourceInstanceId || ""}`;
  const activeTransportScope = useRef(transportScope);
  activeTransportScope.current = transportScope;
  const cicd = useCicdRun(transportScope);
  useEffect(() => () => { activeTransportScope.current = null; }, []);
  const [pdCounts, setPdCounts] = useState({ pids: 0, chained: 0 });
  const [phases, setPhases] = useState([]);
  const startedRef = useRef(false);
  const [sapConfirmed, setSapConfirmed] = useState(false);
  const [packageMaintenance, setPackageMaintenance] = useState(false);

  const pipe = usePipelineReady(state.spaceId);
  // Schalter je Space (Vertrag 24.1): eingeschaltet legt der Wizard nur in die Pipeline, ohne Dispatch. Gelesen wird er,
  // sobald ein Paket zur Prüfung steht. Solange er nicht bestätigt ist, bleibt der heutige Ablauf; der Server lehnt den
  // bisherigen Weg mit Schalter ohnehin ab.
  const needsSwitch = !!state.spaceId && !!(state.pkgId || state.selectedPackages?.length);
  const stageSwitch = useStageBranchesConfig(needsSwitch ? state.spaceId : null);
  const stageMode = stageSwitch.enabled;
  const snapshotExecution = stageMode && !multiPackage && stageSwitch.config?.readModel?.enabled === true;
  const reloadSwitch = stageSwitch.reload;
  // Solange der Schalter lädt oder nicht lesbar ist, startet kein Weg: weder Einzelstart noch Batch.
  const switchLoading = needsSwitch && stageSwitch.loading;
  const switchUnreadable = needsSwitch && !stageSwitch.loading && !!stageSwitch.error;
  const switchUnknown = switchLoading || switchUnreadable;
  // SAP mode: transport runs through SAP's own workflows. No Partner Directory there.
  const sapModus = pipe.modus === "sap";
  const forwardStages = vorwaertsZiele(state);
  const devStage = (state.space?.stages || []).find(stage => stage.stageKey === "dev" && stage.instanceId === state.sourceInstanceId);
  // Der Stufen-Snapshot nimmt nur den Stand der DEV-Instanz (Vertrag 12); eine andere Quelle wäre STAGE_INPUT_INVALID.
  const stageSourceInvalid = stageMode && !devStage;
  const devCapabilities = useDevPdEdgeCapabilities(canonicalTopicSpace(state), devStage?.instanceId,
    !sapModus && pipe.ready && !pipe.loading && state.pdReplicate && !!state.pdSelectedPids?.length);
  const edgeCapabilities = usePdEdgeCapabilities(pdEdgeCapabilityStages(state), !sapModus && pipe.ready && !pipe.loading, state.spaceId);
  const edgeDefaultsPending = usePdEdgeDefaults(state, dispatch, edgeCapabilities.values,
    !sapModus && pipe.ready && !pipe.loading, startedRef.current || !["idle", "failed"].includes(cicd.state) || cicd.cloudComplete);
  const edgeBlocked = edgeDefaultsPending || edgePlanBlocked(state, edgeCapabilities.values);

  const iflows = useMemo(() => selectedIflows(state), [state]);
  // Pakettransport: Zähler, Commit-Text und PD-Erkennung nutzen dieselbe Liste wie die Nutzlast.
  const wholePackage = isPackageTransport(state);
  const transportIflowIds = useMemo(() => (wholePackage ? packageIflowIds(state) : iflows.map((f) => f.id)), [wholePackage, state, iflows]);
  const iflowCount = transportIflowIds.length;
  // Paketinfos im Ziel nur im Artefaktweg; die Version der Erweiterung wird erst gelesen, wenn es etwas zu zeigen gibt.
  // Pakettransport: die Version steuert auch den CI Schalter (Scene3 setzt SET_EXTENSION_VERSION).
  const packageInfoShown = !multiPackage && pipe.ready && !pipe.loading && !sapModus && !wholePackage && hasPackageInfoAlignment(state);
  const extensionVersion = useExtensionVersion((packageInfoShown || wholePackage) ? state.spaceId : null);
  // Dieselbe Liste speist Anzeige und Snapshot-Payload - beide zeigen also dasselbe.
  const gewaehlteRefs = useMemo(() => collectGlobalRefs(state), [state]);
  const deploySelection = deploymentSelection(state);
  const effectivePlans = Object.values(deploySelection.stages || { default: deploySelection });
  const deployDecisions = effectivePlans.flatMap(plan => Object.values(plan.artifacts).flatMap(plants => Object.values(plants)));
  const manualEdgeCount = effectivePlans.flatMap(plan => Object.values(plan.manualEdgeArtifacts).flatMap(plants => Object.values(plants))).filter(Boolean).length;
  const deploymentModeConflict = sapModus && (deployDecisions.some(value => !value) || manualEdgeCount > 0);
  const targetPlanError = useMemo(() => {
    try {
      for (const draft of multiPackage ? packageStates(state) : [state]) plantTargetRevisions(draft);
      return null;
    } catch (error) { return error; }
  }, [state, multiPackage]);
  const topicSpaceId = canonicalTopicSpace(state);
  const topicData = useSpaceTopics(topicSpaceId, !!state.pkgId && pipe.ready && !pipe.loading && !targetPlanError, true);
  const topicId = selectedTopicId(state);
  const topicSelection = selectedTopicSelection(state);
  // Eine Regel für die Topicpflicht (Vertrag 26.1): mit Schalter immer Pflicht und ohne „Standard DEV“.
  const topicRule = topicRequirement({ requireTopicSelection: topicData.settings?.requireTopicSelection === true, stageBranchesEnabled: stageMode });
  const topicRequired = topicRule.required;
  // Stufenmodus ohne wählbares Topic: kein leeres Pflichtfeld, sondern der Weg zum Anlegen (Vertrag 26.1).
  const noSelectableTopics = stageMode && !topicData.loading && !topicData.error
    && !(topicData.topics || []).some(topic => !topic.archived && topic.selectable !== false);

  const preflightScope = useMemo(() => {
    if (targetPlanError) return { payload: null, error: null };
    try {
      return { error: null, payload: {
        spaceId: state.spaceId, sourceInstanceId: state.sourceInstanceId,
        targetStage: transportTargetStage(state.space), expectedTransportModus: sapModus ? "sap" : "erweitert", ...topicSelectionPayload(state),
        packages: (multiPackage ? packageStates(state) : [state]).map(draft => batchPackagePayload(draft, sapModus)),
      } };
    } catch (error) { return { payload: null, error }; }
  }, [state, targetPlanError, sapModus, multiPackage]);
  const topicSelectionBlocked = topicData.loading || !!topicData.error
    || (topicRequired && (!topicSelection || (!topicRule.allowDefault && topicSelection === "default")))
    || (!!topicId && !topicData.topics.some(topic => topic.id === topicId && !topic.archived && topic.selectable !== false));

  // Automatisch erkannte Pids (aus BPMN + ProcessDirect-Reverse-Match)
  const [pdAuto, setPdAuto] = useState({ loading: false, primary: [], related: [], error: null });
  // Sammel-Checkbox "Partner Directories replizieren": an heisst "mindestens eine Pid gewaehlt",
  // die erkannten Pids sind die Vorbelegung, auf die "an" zurueckfuehrt.
  const erkanntePids = useMemo(
    () => [...new Set([...(pdAuto.primary || []), ...(pdAuto.related || [])].map((p) => p.pid).filter(Boolean))],
    [pdAuto]
  );
  const pdGewaehlt = useMemo(() => new Set(state.pdSelectedPids || []), [state.pdSelectedPids]);
  // Ohne erkannte Pids spiegelt die Checkbox das Flag direkt - sonst wirkte ein Klick nicht.
  // Davids Vorgabe 29.10.2026: verkettete Pids werden angezeigt, aber nie vorausgewählt; nur direkt referenzierte.
  const primaerPids = useMemo(() => [...new Set((pdAuto.primary || []).map((p) => p.pid).filter(Boolean))], [pdAuto]);
  const pdAn = !!state.pdReplicate && (pdGewaehlt.size > 0 || erkanntePids.length === 0);
  // Vollstaendige Kandidaten-Liste vom Quell-Tenant (nur PIDs mit Metadaten)
  const [pdSource, setPdSource] = useState({ loading: false, entries: [], error: null });
  const [pdCustomOpen, setPdCustomOpen] = useState(() => !!state.pdSelectedPids?.length);
  const [pdCatalogRequested, setPdCatalogRequested] = useState(() => !!state.pdSelectedPids?.length);
  useEffect(() => {
    if (state.pdSelectedPids?.length) {
      setPdCustomOpen(true);
      setPdCatalogRequested(true);
    }
    // Restore an existing package draft, not a new automatic detection result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pkgId]);
  const [pdSearch, setPdSearch] = useState("");
  const iflowsKey = useMemo(() => [...transportIflowIds].sort().join("|"), [transportIflowIds]);
  // Merker fuer die Vorbelegung: pro Paket+iFlow-Signatur genau EINMAL. Sonst
  // wuerde ein bewusstes Abwaehlen bei jedem Re-Render zurueckgesetzt.
  const prefillKeyRef = useRef(state.pdPrefillKey || null);
  // „Erneut ermitteln“ / „Erneut laden“ (02.11.2026): ein Zähler je Ladeweg startet den Effekt noch einmal.
  const [pdAutoAttempt, setPdAutoAttempt] = useState(0);
  const [pdSourceAttempt, setPdSourceAttempt] = useState(0);

  // Erkannte Pids + Zaehlung laden (aus einer Antwort).
  // Antwortform (siehe backend/server.js /api/transport/partner-references):
  //   partners:        primary + related (aufgeloeste Kette)
  //   primaryPartners: direkt referenzierte Pids - entweder als String (klassisch)
  //                    ODER als Objekt { pid, via: "reference"|"header", sender, iface, msgType }
  //                    (Backend-Erweiterung: Header-Erkennung SAP_Sender/SAP_SenderInterface).
  //   relatedPartners: verkettete Pids (aus Determination-Aufloesung)
  //   partnerRoles:    { pid: "primary"|"related" }
  useEffect(() => {
    if (sapModus) return;
    if (!state.sourceInstanceId || !state.pkgId) return;
    if (!transportIflowIds.length) {
      setPdAuto({ loading: false, primary: [], related: [], error: null });
      setPdCounts({ pids: 0, chained: 0 });
      return;
    }
    let cancelled = false;
    setPdAuto((s) => ({ ...s, loading: true, error: null }));
    const controller = new AbortController();
    // Die Ermittlung lädt am Quell-Tenant iFlow-ZIPs und liest das Partner Directory; auf großen Tenants dauert das
    // Minuten. Deshalb die lange Frist statt der 30 s Vorgabe von api().
    api("/api/transport/partner-references", {
      method: "POST", timeout: PD_LOAD_TIMEOUT_MS, retry: false, signal: controller.signal,
      body: JSON.stringify({
        sourceInstanceId: state.sourceInstanceId,
        targetInstanceId: state.space?.qasInstance?.id,
        sourcePackageId: state.pkgId,
        artifactIds: transportIflowIds,
      }),
    })
      .then((r) => {
        if (cancelled) return;
        // Beide Formen tolerant lesen: Objekt-Eintraege behalten via/sender/iface,
        // Strings werden auf {pid, via:"reference"} normalisiert. Bei Duplikaten
        // (selber Pid, einmal via reference, einmal via header) gewinnt der erste.
        const norm = (arr) => {
          const out = []; const gesehen = new Set();
          for (const raw of Array.isArray(arr) ? arr : []) {
            let pid, via = "reference", sender = null, iface = null, msgType = null;
            if (typeof raw === "string") { pid = raw; }
            else if (raw && typeof raw === "object") {
              pid = raw.pid || raw.Pid || raw.id;
              via = raw.via || "reference";
              sender = raw.sender || null;
              iface = raw.iface || raw.interface || null;
              msgType = raw.msgType || raw.messageType || null;
            }
            if (!pid || gesehen.has(pid)) continue;
            gesehen.add(pid);
            out.push({ pid, via, sender, iface, msgType });
          }
          return out;
        };
        // Das Backend liefert via/Header als Top-Level-Maps (pid-basiert), nicht am Eintrag:
        //   partnerVia:     { [pid]: ["header"|"address"|"reference"] }
        //   partnerHeaders: { [pid]: { sender, iface, msgType } }
        // Hier auf die pro-Eintrag-Form ziehen, die das UI erwartet (Header-Kontext-Hinweis).
        // Sind die Eintraege schon Objekte mit via/sender (kuenftige Backend-Form), bleibt das erhalten.
        const viaMap = r?.partnerVia || {};
        const hdrMap = r?.partnerHeaders || {};
        const anreichern = (p) => {
          const w = viaMap[p.pid];
          const h = hdrMap[p.pid];
          const via = Array.isArray(w) ? (w.includes("header") ? "header" : (w[0] || p.via)) : p.via;
          return { ...p, via, sender: h?.sender ?? p.sender, iface: h?.iface ?? p.iface, msgType: h?.msgType ?? p.msgType };
        };
        const primary = norm(r?.primaryPartners).map(anreichern);
        const related = norm(r?.relatedPartners).map(anreichern);
        setPdAuto({ loading: false, primary, related, error: null });
        setPdCounts({ pids: primary.length, chained: related.length });
        // Vorbelegung: genau EINMAL pro (Paket + iFlow-Signatur). Ein bewusstes
        // Abwaehlen soll erhalten bleiben - der Merker verhindert das erneute
        // Ueberschreiben, weil der Effekt bei jeder Response neu laeuft.
        // Nur direkt referenzierte Pids vorbelegen; verkettete bleiben sichtbar, aber abgewählt.
        const detectedPids = [...new Set(primary.map((p) => p.pid))];
        const key = `${state.pkgId}::${iflowsKey}`;
        if (prefillKeyRef.current !== key) {
          prefillKeyRef.current = key;
          dispatch({ type: "SET_PD_SELECTED_PIDS", payload: detectedPids });
          dispatch({ type: "SET_PD_PREFILL_KEY", payload: key });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setPdAuto({ loading: false, primary: [], related: [], error: pdLoadError(e, t, "s5.pdAuto") });
        setPdCounts({ pids: 0, chained: 0 });
      });
    return () => { cancelled = true; controller.abort(); };
    // Wir haengen bewusst NICHT an state.pdSelectedPids - sonst wuerde jedes
    // Abwaehlen den Effekt neu triggern und die Vorbelegungslogik verwirren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sourceInstanceId, state.pkgId, state.space, iflowsKey, dispatch, sapModus, pdAutoAttempt]);

  // Volle PD-Liste vom Quell-Tenant laden (fuer manuelles Hinzufuegen).
  // Antwortform: { partners: [{ Pid, stringParams:[{Id,Value}], binaryParams:[{Id,ContentType}], altPartners:[...], systemGrund }] }
  // systemGrund != null -> systemeigene Kennung, nicht auswaehlbar.
  useEffect(() => {
    if (sapModus) return; // SAPs Weg traegt kein Partner Directory - Quell-Tenant nicht befragen
    if (!state.sourceInstanceId || !pdCatalogRequested) return;
    let cancelled = false;
    setPdSource((s) => ({ ...s, loading: true, error: null }));
    const controller = new AbortController();
    // Gleiche Frist wie die Ermittlung, ohne die GET-Wiederholungen von api(): ein langsamer Tenant soll die Liste
    // nicht viermal nacheinander laden. Das Wiederholen bleibt dem Benutzer („Erneut laden“).
    api(`/api/${state.sourceInstanceId}/partner-directory`, { timeout: PD_LOAD_TIMEOUT_MS, retry: false, signal: controller.signal })
      .then((r) => {
        if (cancelled) return;
        if (!Array.isArray(r?.partners)) throw new Error("Invalid partner directory response");
        setPdSource({ loading: false, entries: r.partners, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setPdSource({ loading: false, entries: [], error: pdLoadError(e, t, "s5.pdCustom") });
      });
    return () => { cancelled = true; controller.abort(); };
  }, [state.sourceInstanceId, sapModus, t, pdSourceAttempt, pdCatalogRequested]);

  // Jedes Paket des Laufs (im Mehrpaketmodus alle Entwürfe, nicht nur das aktive) muss startbar sein.
  const packagesReady = !!preflightScope.payload?.packages.length && preflightScope.payload.packages.every(packageStartable);
  // Ein gescheitertes Laden des Partner Directory sperrt das Einlegen nicht mehr (Vorgabe David 02.11.2026: keine
  // Webapp-Sperre, ablehnen darf nur der Server beim Einlegen bzw. GitHub). Solange es läuft, fehlt die Vorbelegung
  // der Partner-IDs noch; so lange wartet der Start.
  const pdLoading = !sapModus && (pdAuto.loading || pdSource.loading);
  const preflight = useTransportPreflight({ payload: preflightScope.payload,
    enabled: !!state.pkgId && pipe.ready && !pipe.loading && !targetPlanError && !preflightScope.error
      && !edgeBlocked && !pdLoading && packagesReady });
  const reloadTopicSettings = topicData.reload;
  // Ohne Vorprüfung (02.11.2026) lehnt erst das Einlegen ab; die fehlende Paket-Einrichtung behält ihre Abhilfe mit Link.
  const setupRejection = (cicd.failure?.code || cicd.failure?.body?.code) === "PACKAGE_TRANSPORT_SETUP_REQUIRED" ? cicd.failure : null;
  const startError = preflight.error || setupRejection;
  const staleTopicSettingsError = preflight.error?.code === "TOPIC_SELECTION_REQUIRED" ? preflight.error
    : cicd.failure?.code === "TOPIC_SELECTION_REQUIRED" ? cicd.failure : null;
  const refreshedTopicError = useRef(null);
  const [topicSettingsRefreshing, setTopicSettingsRefreshing] = useState(false);
  useEffect(() => {
    if (staleTopicSettingsError && refreshedTopicError.current !== staleTopicSettingsError) {
      refreshedTopicError.current = staleTopicSettingsError; setTopicSettingsRefreshing(true);
      reloadTopicSettings().finally(() => setTopicSettingsRefreshing(false));
    }
  }, [staleTopicSettingsError, reloadTopicSettings]);
  const topicBlocked = topicSelectionBlocked || !!preflightScope.error;
  // Lehnt die Vorprüfung mit Schalter ab (Vertrag 24.1), liest der Wizard den Schalter neu und wechselt den Weg.
  useEffect(() => { if (isStageBranchesActive(preflight.error)) reloadSwitch(); }, [preflight.error, reloadSwitch]);

  const startTransport = useCallback(async () => {
    if (switchUnknown || stageSourceInvalid || packageMaintenance || startedRef.current || cicd.cloudComplete || !preflight.canCheck || cicd.state === "unknown" || topicBlocked || edgeBlocked || targetPlanError || !validPlantNaming(state.plantNaming) || !plantChangesConfirmed(state) || deploymentModeConflict || pipe.loading || !pipe.ready || (sapModus && !sapConfirmed)) return;
    startedRef.current = true;
    const startedScope = activeTransportScope.current;
    // Keine Vorprüfung beim Start (Vorgabe David 02.11.2026): „In die Pipeline legen“ legt sofort ein; ablehnen darf
    // nur der Server beim Einlegen bzw. GitHub.
    dispatch({ type: "LOCK_PD_EDGE_DEFAULTS" });
    const phaseList = [
      { id: "git", title: t("ph.git"), icon: "ic-git", logs: [] },
      ...(stageMode ? [] : [{ id: "wf", title: t("ph.wf"), icon: "ic-play", logs: [] }]),
    ];
    setPhases(phaseList.map((p) => ({ ...p, status: "waiting" })));

    const setPhase = (id, patch) => setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    const appendLog = (id, line) => setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, logs: [...p.logs, line] } : p)));

    // Phase git
    setPhase("git", { status: "running" });
    let failedPhase = "git";
    let snapshotRequested = false;
    try {
      const packagePayload = batchPackagePayload(state, sapModus);
      const targetStageKey = transportTargetStage(state.space);
      snapshotRequested = true;
      const res = await cicd.start({
        ...packagePayload,
        spaceId: state.spaceId,
        userId: user?.id,
        canonicalSpaceId: topicSpaceId,
        ...topicSelectionPayload(state),
        sourceInstanceId: state.sourceInstanceId,
        expectedTransportModus: sapModus ? "sap" : "erweitert",
        sapWholePackageConfirmed: sapModus && sapConfirmed,
        snapshotPartnerIds: packagePayload.partnerIds,
        partnerIds: pdPidsFuerStufe({ pdSelectedPids: packagePayload.partnerIds, pdStagesByPid: packagePayload.pdStagesByPid }, targetStageKey),
        commitMessage: `Transport ${state.pkgId} (${iflowCount} artifacts)`,
        targetStage: targetStageKey,
        stageBranches: stageMode,
      }, { onSnapshotConfirmed: () => {
        if (activeTransportScope.current !== startedScope) return;
        appendLog("git", t("log.commit"));
        setPhase("git", { status: "done" });
        if (stageMode) return;
        failedPhase = "wf";
        setPhase("wf", { status: "running" });
      } });

      if (activeTransportScope.current !== startedScope) return;

      if (res.staged) {
        appendLog("git", t("s5.stagedLog"));
      } else if (res.pendingApproval) {
        appendLog("wf", t("log.pending"));
        setPhase("wf", { status: "pending" });
      } else {
        appendLog("wf", t("log.wf", {
          wf: sapModus ? "btp-sap-transport.yml" : "btp-artifact-transport.yml",
          s: targetStageKey,
        }));
        appendLog("wf", res.runId ? t("log.queued", { n: res.runId }) : t("run.noRunFound"));
        setPhase("wf", { status: res.runId && res.dispatchConfirmed !== false ? "done" : "unknown" });
        if (res.dispatchConfirmed === false && res.runId) appendLog("wf", t("log.dispatchUnconfirmed"));
        if (!res.runId) startedRef.current = false;
      }
    } catch (e) {
      if (activeTransportScope.current !== startedScope) return;
      // Schalter inzwischen an: neu lesen, damit der Wizard selbst auf das Einlegen wechselt (Vertrag 24.1).
      if (isStageBranchesActive(e)) reloadSwitch();
      if (!state.pdEdgeDefaultsLocked && (!snapshotRequested || e.snapshotRejected === true))
        dispatch({ type: "UNLOCK_PD_EDGE_DEFAULTS", payload: true });
      const failedId = ["git", "wf"].includes(e.transportPhase) ? e.transportPhase : failedPhase;
      setPhase(failedId, { status: e.transportOutcomeUnknown ? "unknown" : "error", error: plantNamingError(e, lang) || "Fehler" });
      startedRef.current = false;
    }
  }, [state, dispatch, iflowCount, cicd, t, lang, sapModus, sapConfirmed, deploymentModeConflict, pipe.loading, pipe.ready, edgeBlocked, targetPlanError, topicBlocked, topicSpaceId, preflight, packageMaintenance, stageMode, stageSourceInvalid, switchUnknown, reloadSwitch]);

  const canStart = preflight.canCheck && !topicBlocked && packagesReady && !!state.sourceInstanceId && !!state.pkgId && !targetPlanError && plantChangesConfirmed(state) && validPlantNaming(state.plantNaming);
  const running = cicd.state === "confirming" || cicd.state === "snapshotting" || cicd.state === "dispatching" || cicd.state === "running" || cicd.state === "syncing_edge";
  // A pending snapshot is saved in Git; the request is created on the approvals page.
  const staged = cicd.state === "staged";
  // Abgelegt (bisheriger Freigabeweg oder Stufen-Board): der Wizard ist für diesen Stand fertig.
  const snapshotPending = cicd.state === "pending" || staged;
  // Im SAP-Modus laeuft der Transport ueber SAPs Workflows: paketweise, mit Loeschen und ohne
  // Partner Directory. Die Werke bleiben - ihr Split passiert im Snapshot, nicht im Workflow.
  const startBtnDisabled = switchUnknown || stageSourceInvalid || packageMaintenance || !canStart || edgeBlocked || pdLoading || running || snapshotPending || cicd.cloudComplete || cicd.state === "unknown" || cicd.state === "done" || cicd.state === "dispatched" || pipe.loading || !pipe.ready || deploymentModeConflict || (sapModus && !sapConfirmed);

  const transportLocked = running || snapshotPending || cicd.cloudComplete || ["unknown", "done", "dispatched"].includes(cicd.state) || !!state.batch || !!state.batchRequest;
  const reviewLocked = packageMaintenance || preflight.loading || running || snapshotPending || cicd.cloudComplete || cicd.state === "unknown" || cicd.state === "done" || cicd.state === "dispatched" || !!state.batch || !!state.batchRequest;
  const s = state.space;
  const stages = s?.stages || [];
  const tgtIdx = transportTargetIndex(s);

  const runJobs = useMemo(() => {
    if (cicd.runInfo?.jobs?.length) {
      const flat = [];
      for (const j of cicd.runInfo.jobs) {
        for (const st of j.steps || []) {
          flat.push({ name: st.name, status: st.status, duration: st.completed_at && st.started_at ? Math.max(1, Math.round((new Date(st.completed_at) - new Date(st.started_at)) / 1000)) + "s" : "" });
        }
      }
      return flat.slice(0, 12);
    }
    return [];
  }, [cicd.runInfo]);

  const runNumber = cicd.runInfo?.runNumber || cicd.runId || null;
  const runStatus = cicd.state === "done" || cicd.cloudComplete ? "success" : cicd.state === "failed" ? "failed" : "running";
  const runMeta = cicd.runInfo?.actor ? `${cicd.runInfo.actor} · ${cicd.runInfo.event || "workflow_dispatch"}` : "";
  const done = cicd.state === "done";

  useEffect(() => {
    if (done) dispatch({ type: "SET_PIPELINE_STATE", payload: { pipeline: "done" } });
  }, [done, dispatch]);

  return (
    <section className="scene active scene-enter review-mui" id="s5">
      <div className="sh">
        <h2>{t("s5.h2")}</h2>
        <p>{t(stageMode ? "s5.pStage" : "s5.p")}</p>
      </div>

      <div className="manifest">
        <div className="mf-cell">
          <span className="mf-k">{t("mf.space")}</span>
          <span className="mf-v">{s?.name || "-"}</span>
          <span className="mf-s">
            {stages.map((st, i) => (
              <React.Fragment key={"m" + st.code + i}>
                {i > 0 && <Cx />}<span>{st.code}</span>
              </React.Fragment>
            ))}
          </span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.pkg")}</span>
          <span className="mf-v">{state.pkgName || state.pkgId || "-"}</span>
          {state.pkgName && state.pkgName !== state.pkgId && <span className="mf-s">{state.pkgId || ""}</span>}
          {wholePackage && <span className="mf-s">{t("pkgT.mode")}: {t("pkgT.modeWhole")}</span>}
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.iflows")}</span>
          <span className="mf-v">{t("mf.sel", { n: iflowCount })}</span>
          <span className="mf-s">
            {usesWerke(state)
              ? t("mf.rowsW", { n: Object.values(state.sel || {}).reduce((acc, s2) => acc + Object.values(s2 || {}).filter(Boolean).length, 0), m: activeWerke(state).length })
              : t("mf.rows", { n: Object.values(state.sel || {}).reduce((acc, s2) => acc + Object.values(s2 || {}).filter(Boolean).length, 0) })}
          </span>
          {!sapModus && <DeploymentSummary selection={deploySelection}/> }
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.refs")}</span>
          <span className="mf-v">{sapModus ? "-" : gewaehlteRefs.length}</span>
          <span className="mf-s">{sapModus ? t("batch.sapReferences") : gewaehlteRefs.map((r) => (r.type ? `${r.id} (${r.type})` : r.id)).join(", ") || "-"}</span>
        </div>
        <div className="mf-cell">
          <span className="mf-k">{t("mf.cfg")}</span>
          <span className="mf-v">{t("mf.req", { n: 0 })}</span>
          <span className="mf-s">{t("mf.allset")}</span>
        </div>
      </div>

      {wholePackage && !multiPackage && <WholePackageReview state={state} />}

      <div className="chevflow">
        <span>{t("cf.iflows", { n: iflowCount })}</span>
        <Cx />
        <span>{state.pkgName || state.pkgId || ""}</span>
        {usesWerke(state) && <><Cx /><span>{t("rt.werke")} <span className="ken">{activeWerke(state).map((w) => w.kennung).join(" ")}</span></span></>}
        <Cx />
        <span>{stages[tgtIdx]?.code || stageLabel(transportTargetStage(s))} · {stages[tgtIdx]?.inst || ""}</span>
        <Cx />
        <span>{stages[stages.length - 1]?.code || "PROD"} · {stages[stages.length - 1]?.inst || ""}</span>
      </div>

      {!pipe.loading && !pipe.ready && (
        <div className="pipe-block">
          <svg className="ic"><use href="#ic-info" /></svg>
          <div className="pb-tx">
            <b>{pipe.forbidden ? t("s0.pipelineDenied") : pipe.error ? t("s0.pipelineReadFailed") : t("s5.noPipeTitle")}</b>
            {!pipe.error && !pipe.forbidden && <div className="pb-sub">{pipe.configured && !pipe.tokenSet ? t("s5.noPipeToken") : t("s5.noPipeCfg")}</div>}
          </div>
          {!pipe.error && !pipe.forbidden && pipe.canManage && <Button variant="outlined" component="a" href="/cicd-setup">{t("s5.noPipeLink")}</Button>}
        </div>
      )}

      {/* SAP-Modus: SAPs Transport traegt kein Partner Directory mit. Der Schalter wird deshalb
          ausgeblendet, nicht ausgegraut - ein Bedienelement ohne Wirkung ist schlimmer als
          keines. Stattdessen ein Satz, der sagt, was hier anders laeuft. */}
      {(pipe.loading || pipe.ready) && sapModus && (
        <div className="pd-row" style={{ borderLeft: "2px solid var(--warn)", paddingLeft: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <label>{t("s5.sapModus")}</label>
            <div className="pd-sub">{t("s5.sapModusSub")}</div>
            {deploymentModeConflict && <div className="pd-sub" role="alert">{t("deploy.sap")}</div>}
          </div>
        </div>
      )}
      {(pipe.loading || pipe.ready) && !sapModus && (<fieldset className="batch-pd-fields">
      <div className="pd-row">
        <Checkbox checked={pdAn} disabled={reviewLocked}
          indeterminate={pdAn && primaerPids.some(pid => !pdGewaehlt.has(pid))}
          inputProps={{ "aria-label": t("s5.pd") }}
          onChange={event => {
            dispatch({ type: "SET_PD_REPLICATE", payload: event.target.checked });
            if (event.target.checked && primaerPids.length) dispatch({ type: "SET_PD_SELECTED_PIDS", payload: primaerPids });
          }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <label>{t("s5.pd")}</label>
          <PartnerDirectoryInfo />
          <div className="pd-sub">{pdAn ? t("s5.pdSub", { n: pdCounts.pids || 0, m: pdCounts.chained || 0 }) : t("s5.pdNoneSelected")}</div>
        </div>
      </div>

        <PdPicker
          t={t}
          pdAuto={pdAuto}
          pdSource={pdSource}
          customOpen={pdCustomOpen}
          onCustomOpen={open => {
            setPdCustomOpen(open);
            if (open) setPdCatalogRequested(true);
          }}
          onRetryAuto={() => setPdAutoAttempt(n => n + 1)}
          onRetryCustom={() => setPdSourceAttempt(n => n + 1)}
          pdSearch={pdSearch}
          setPdSearch={setPdSearch}
          selectedPids={state.pdSelectedPids || []}
          pdStagesByPid={state.pdStagesByPid || {}}
          // Bei einer einzigen Vorwaerts-Stufe waere ein Umschalter sinnlos - PdPicker blendet
          // ihn dann komplett aus und die Ansicht ist wie heute.
          forwardStages={forwardStages}
          pdEdgeByPid={state.pdEdgeByPid || {}}
          pdEdgeChoicesByPid={state.pdEdgeChoicesByPid || {}}
          pdEdgeModeByPid={state.pdEdgeModeByPid || {}}
          conflictingEdgePids={pdEdgeConflictPids(state)}
          devStage={devStage ? { ...devStage, name: devStage.inst || devStage.name || state.space?.sourceInstance?.name } : null}
          devSpaceId={canonicalTopicSpace(state)} devCapabilities={devCapabilities}
          onSetEdgeMode={(pid, mode, targets) => dispatch({ type: "SET_PD_EDGE_MODE", payload: { pid, mode, targets } })}
          edgeDefaultsEnabled={!reviewLocked && !state.pdEdgeDefaultsLocked}
          edgeCapabilities={edgeCapabilities}
          disabled={reviewLocked} devDisabled={transportLocked}
          onSetEdge={(pid, stage, runtimeId) => dispatch({ type: "SET_PD_EDGE", payload: { pid, stage, runtimeId } })}
          onToggle={(pid) => dispatch({ type: "TOGGLE_PD_PID", payload: pid })}
          onSetAll={(pids) => dispatch({ type: "SET_PD_SELECTED_PIDS", payload: pids })}
          onToggleStage={(pid, stage) => dispatch({ type: "TOGGLE_PD_STAGE", payload: { pid, stage } })}
        />

        {pdGewaehlt.size > 0 && <Box className="pd-edge-note"><p>{t(stageMode ? "pdEdge.sequenceStage" : "pdEdge.sequence")}</p>
          {Object.keys(pdEdgeSelection(state)).length > 0 && <p>{t("pdEdge.snapshotHint")}</p>}</Box>}
        {!edgeDefaultsPending && edgeBlocked && <Alert severity="warning">{t("pdEdge.blocked")}</Alert>}
        <HiddenEdgeTargets capabilities={edgeCapabilities.values} disabled={reviewLocked} t={t} />
      </fieldset>)}

      {/* Paketrückzug ist mit Schalter serverseitig gesperrt (Vertrag 24.1); kein Bedienelement, das abgelehnt würde. */}
      {!stageMode && <ReviewPackageRetirement key={`${transportScope}:${state.packageEpoch}:${JSON.stringify(retirementTargets(state))}`}
        targets={retirementTargets(state)} user={user} spaceId={state.spaceId} canonicalSpaceId={topicSpaceId}
        blocked={preflight.loading || running || snapshotPending || ["unknown", "dispatched"].includes(cicd.state) || !!state.batch || !!state.batchRequest || switchUnknown}
        onLockChange={setPackageMaintenance} onRemoved={() => { cicd.stop(); dispatch({ type: "RESET_ALL" }); }}/>}
      <PlantChangeConfirmation disabled={reviewLocked} />
      {/* Nur Artefaktweg: Im SAP-Modus und beim ganzen Paket ersetzt SAP das Zielpaket samt Paketinfos ohnehin. */}
      {packageInfoShown && <PackageInfoAlignment state={state} extensionVersion={extensionVersion} />}
      {pipe.ready && !targetPlanError && noSelectableTopics && <Alert severity="warning" sx={{ overflowWrap: "anywhere" }}
        action={<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {/* Neuer Tab, damit der Wizardstand erhalten bleibt; danach holt „Erneut lesen“ das neue Topic. */}
          <Button color="inherit" size="small" component="a" href="/" target="_blank" rel="noopener">{t("s5.spacesOpen")}</Button>
          <Button color="inherit" size="small" disabled={reviewLocked} onClick={() => topicData.reload()}>{t("s5.topicsReload")}</Button>
        </Box>}>
        <AlertTitle>{t("s5.noTopics")}</AlertTitle>{t("s5.noTopicsHint")}</Alert>}
      {pipe.ready && !targetPlanError && !noSelectableTopics && <TopicSelection {...topicData} value={topicId} selection={topicSelection} allowDefault={topicRule.allowDefault} required={topicRequired || (!!staleTopicSettingsError && (topicSettingsRefreshing || !!topicData.error))} disabled={reviewLocked}
        reload={topicData.reload}
        onChange={(value, selection) => dispatch({ type: "SET_TOPIC", payload: { spaceId: topicSpaceId, topicId: value, topicSelection: selection } })}/>}
      {preflight.loading && <Alert severity="info" icon={<CircularProgress size={20} aria-label={t("s5.preflightLoading")} />}>{t("s5.preflightLoading")}</Alert>}
      {startError && <Alert severity="error" sx={{ overflowWrap: "anywhere" }}>
        {(startError.body?.targetPackageId || startError.body?.packageId) && <PreflightPackage state={state}
          packageId={startError.body.targetPackageId || startError.body.packageId} />}
        {transportPreflightError(startError, lang)}
        {/* Abhilfe: Link im Text zum bestehenden „Aktualisieren“ der Erweiterung im CI/CD Monitor, kein eigener Knopf. */}
        {(startError.code || startError.body?.code) === "PACKAGE_TRANSPORT_SETUP_REQUIRED" && state.spaceId && <>{" "}
          <Link href={`/cicd-pipeline?spaceId=${encodeURIComponent(state.spaceId)}#cp-repository`} target="_blank" rel="noopener"
            color="inherit" underline="always" sx={{ fontWeight: 600, "&:focus-visible": { outline: "2px solid var(--iio-signal)", outlineOffset: 2 } }}>
            {packageTransportText("setupLink", lang)}</Link></>}
      </Alert>}
      {preflightScope.error && <Alert severity="error">{transportPreflightError(preflightScope.error, lang)}</Alert>}
      {targetPlanError && <Alert severity="error">{plantNamingError(targetPlanError, lang)}</Alert>}
      {/* Mit Schalter bleibt der Batchweg gesperrt, weil er sofort transportiert (Vertrag 24.1): Pakete einzeln einlegen. */}
      {switchUnreadable && <Alert severity="error" action={<Button color="inherit" size="small" onClick={reloadSwitch}>{t("s5.switchReload")}</Button>}>
        {t("s5.switchUnreadable")}</Alert>}
      {multiPackage && switchLoading && <LinearProgress aria-label={t("s5.switchLoading")}/>}
      {multiPackage && stageMode && !switchUnknown && <Alert severity="info">{t("s5.stageSinglePackage")}</Alert>}
      {/* Der Batchweg erscheint erst, wenn der Schalter bestätigt aus ist. */}
      {multiPackage && !stageMode && !switchUnknown && !targetPlanError && !preflightScope.error && <BatchReview onStageBranchesActive={reloadSwitch} onTopicSettingsRequired={reloadTopicSettings} pipe={pipe} preparePackage={batchPackagePayload} preflight={preflight}
        preparationReady={!packageMaintenance && !topicSelectionBlocked && plantChangesConfirmed(state) && state.configReviewed && packagesReady && !deploymentModeConflict && !edgeBlocked && !pipe.loading && pipe.ready
          && !pdLoading} />}
      {!multiPackage && sapModus && <FormControlLabel className="batch-confirm" label={t("batch.sapConfirm")} control={<Checkbox checked={sapConfirmed}
        disabled={reviewLocked} onChange={event => setSapConfirmed(event.target.checked)} />} />}

      {/* Der Startknopf steht AUSSERHALB beider Modus-Bloecke: er gehoert zu keinem von beiden,
          und im SAP-Modus gaebe es sonst nichts mehr zu druecken. */}
      {!multiPackage && stageSourceInvalid && <Alert severity="warning">{t("run.stageDevOnly")}</Alert>}
      {/* Eingelegt: der Erfolgshinweis unten trägt den einzigen nächsten Schritt, der gesperrte Knopf entfällt. */}
      {!multiPackage && (pipe.loading || pipe.ready) && !staged && (
        <div className="cta-row">
          <Button variant="contained" disableElevation onClick={startTransport} disabled={startBtnDisabled}>
            {running ? t("s5.running") : snapshotPending ? t("s5.waitApproval")
              : (<><svg className="ic"><use href="#ic-play" /></svg>{t(stageMode ? "s5.startStage" : "s5.start")}</>)}
          </Button>
          {/* Schalter wird gelesen: Knopf gesperrt, neutrale Anzeige statt der Fußnote. */}
          {switchLoading ? <span className="foot-hint" role="status"><CircularProgress size={14} aria-hidden="true" sx={{ mr: 1, verticalAlign: "middle" }}/>{t("s5.switchLoading")}</span>
            : <span className="foot-hint">{stageMode ? t("s5.footHintStage") : `Branch development · ${sapModus ? "btp-sap-transport.yml" : "btp-artifact-transport.yml"}`}</span>}
        </div>
      )}

      {/* Einlegeaufträge gehören in die Laufhistorie, nicht in den Transport (Davids Vorgabe 29.10.2026). */}
      {snapshotExecution && (phases.length > 0 || staged) && <SnapshotExecution
        phase={phases.find(phase => phase.id === "git")} job={cicd.snapshotJob} staged={staged}
        spaceId={state.spaceId} targetStage={stageLabel(transportTargetStage(state.space))}
        meta={state.pdReplicate && (state.pdSelectedPids || []).length > 0
          ? t("s5.metaWith", { user: user?.displayName || user?.username || "system" })
          : t("s5.metaWithout", { user: user?.displayName || user?.username || "system" })}/>}
      {staged && !snapshotExecution && (
        <Alert severity="success" role="status" className="sect">
          <AlertTitle>{t("s5.stagedTitle")}</AlertTitle>
          <Box component="p" sx={{ m: 0, mb: 1.5 }}>{t("s5.stagedHint", { stage: stageLabel(transportTargetStage(state.space)) })}</Box>
          <Button component="a" variant="outlined" color="inherit" href={`/approvals?spaceId=${encodeURIComponent(state.spaceId)}`}>{t("rel.open")}</Button>
        </Alert>
      )}
      {snapshotPending && !staged && (
        <div className="sect panel panel-pad">
          <div className="st st--warn"><i className="dot" />{t("s5.pendingApproval")}</div>
          <div className="pd-sub" style={{ marginTop: 6, color: "var(--t2)" }}>{t("s5.pendingApprovalSub")}</div>
        </div>
      )}

      {!snapshotExecution && phases.length > 0 && (
        <div className="sect">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14, flexWrap: "wrap", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{t("s5.execTitle")}</h3>
            <span className="tiny muted">{state.pdReplicate && (state.pdSelectedPids || []).length > 0 ? t("s5.metaWith", { user: user?.displayName || user?.username || "system" }) : t("s5.metaWithout", { user: user?.displayName || user?.username || "system" })}</span>
          </div>
          <div>
            {phases.map((p) => (
              <div key={p.id} className={"phase-card " + (p.status === "running" ? "run" : p.status === "done" ? "done" : p.status === "error" ? "run" : ["pending", "unknown"].includes(p.status) ? "unconfirmed" : "")}>
                <div className="pc-h">
                  <span className="ph-ind">
                    {p.status === "running" && <CircularProgress size={18} aria-label={p.title} />}
                    {p.status === "done" && <svg className="ic ic-16 ph-check"><use href="#ic-check" /></svg>}
                  </span>
                  <svg className="ic" style={{ color: "var(--t3)" }}><use href={"#" + p.icon} /></svg>
                  <span className="pc-title">{p.title}</span>
                  <span className="tiny muted">{p.status === "done" ? t("s5.done") : p.status === "pending" ? t("s5.notStarted") : p.status === "unknown" ? t("batch.status.unknown") : ""}</span>
                </div>
                <div className="pc-log">
                  {p.logs.map((line, i) => (
                    <div key={i} className="log-line in">{p.status === "done" && <CheckRounded className="ok-mark" fontSize="small" aria-hidden="true"/>}{line}</div>
                  ))}
                  {p.error && <div className="errline">{p.error}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Partial failure: with several plants each target package gets its own run. If one of
          them fails to start the rest keeps going - the user must not miss that, otherwise
          they take the transport for complete. The bar carries the colour, the text stays
          readable (a semantic colour does not carry as text colour).
          Der Block steht bewusst AUSSERHALB der Lauf-Karte: nicht ladbare Artefakte, nicht
          uebernommene Partner-Kennungen und Pakete ohne Lauf muessen auch dann sichtbar sein,
          wenn gar kein Lauf zustande kam. */}
      {(cicd.error || (cicd.meldungen || []).length > 0) && (
        <div className="sect">
          {cicd.error && (
            <div
              role="status"
              style={{
                padding: "8px 12px",
                borderLeft: "2px solid var(--warn)",
                background: "color-mix(in srgb, var(--warn) 8%, transparent)",
                fontSize: 13,
                color: "var(--t1)",
              }}
            >
              {cicd.error}
              <CicdMonitorButton spaceId={state.spaceId} runId={cicd.runId} execution={executionOf(cicd.runInfo)} packageId={state.pkgId}/>
            </div>
          )}
          {(cicd.meldungen || []).length > 0 && (
            <>
              <div className="grp-label" style={{ margin: "14px 0 6px" }}>{t("s5.msgTitle")}</div>
              {cicd.meldungen.map((m, i) => {
                const farbe = m.art === "fehler" ? "var(--crit)" : "var(--warn)";
                return (
                  <div
                    key={i}
                    style={{
                      padding: "6px 12px",
                      marginBottom: 4,
                      borderLeft: `2px solid ${farbe}`,
                      background: `color-mix(in srgb, ${farbe} 7%, transparent)`,
                      fontSize: 12.5,
                      color: "var(--t1)",
                    }}
                  >
                    {m.text}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {(cicd.runId || cicd.runInfo?.id) && (cicd.state === "running" || cicd.state === "syncing_edge" || cicd.state === "done" || cicd.state === "failed") && (
        <div className="sect">
          <RunLiveCard
            stages={stages}
            tgtIdx={tgtIdx}
            runInfo={cicd.runInfo}
            jobs={runJobs}
            runNumber={runNumber}
            runStatus={runStatus}
            runMeta={runMeta}
            done={done}
            doneText={t("s5.qasOk")}
            doneSub={t("s5.qasOkSub")}
          />
        </div>
      )}

      <ExecutionErrors execution={executionOf(cicd.runInfo)}/>
      <ReviewEdgeStatus spaceId={state.spaceId} value={cicd.edgeFollowups} />

      {!multiPackage && state.spaceId && pipe.ready && (done || snapshotPending) && !staged && (
        <section className="sect panel panel-pad" aria-labelledby="s5-approval-title">
          <h3 id="s5-approval-title" style={{ margin: 0, fontSize: 16 }}>{t("rel.title")}</h3>
          <p className="pd-sub" style={{ color: "var(--t2)" }}>{t("rel.hint")}</p>
          <Button component="a" variant="outlined" href={`/approvals?spaceId=${encodeURIComponent(state.spaceId)}`}>
            {t("rel.open")}
          </Button>
        </section>
      )}

      <div className="footbar">
        <Button variant="text" disabled={reviewLocked} onClick={() => dispatch({ type: "SET_STEP", payload: 4 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </Button>
        {!stageMode && <span className="foot-hint">{t("s5.footHint")}</span>}
      </div>
    </section>
  );
}

// ============================================================================
// PdPicker - zwei Bloecke fuer die Partner-Directory-Auswahl:
//   1) Automatisch erkannt (aus BPMN + ProcessDirect-Reverse-Match) - vorausgewaehlt
//   2) Eigene hinzufuegen (volle Liste vom Quell-Tenant, systemGrund ausgegraut)
// Bewusst als Untervkomponente in derselben Datei - kein neues File fuer eine
// Ansicht, die nur in Scene5Review existiert.
// ============================================================================
export function PdPicker({ t, pdAuto, pdSource, customOpen = true, onCustomOpen, onRetryAuto, onRetryCustom, pdSearch, setPdSearch, selectedPids, pdStagesByPid, forwardStages, onToggle, onSetAll, onToggleStage, pdEdgeByPid = {}, pdEdgeChoicesByPid = {}, edgeDefaultsEnabled = false, edgeCapabilities = { values: {}, reload() {} }, onSetEdge, onSetEdgeMode, pdEdgeModeByPid = {}, conflictingEdgePids = [], devStage, devSpaceId, devCapabilities, disabled = false, devDisabled = disabled }) {
  const selSet = new Set(selectedPids);
  // Normalisiert: primary/related sind Arrays von {pid, via, sender, iface, msgType}.
  const primary = pdAuto.primary || [];
  const related = pdAuto.related || [];
  // Pid-Menge (ohne Doppelte) - fuer den Ausschluss aus "Eigene Eintraege".
  const detectedPidSet = new Set([...primary.map((p) => p.pid), ...related.map((p) => p.pid)]);
  // Anzeigereihenfolge: primary vor related; wenn ein Pid in beiden vorkommt,
  // reicht der primaere Eintrag - der related-Duplikat waere doppelte Zeile.
  const primaryPids = new Set(primary.map((p) => p.pid));
  const anzeige = [
    ...primary.map((p) => ({ ...p, rolle: p.via === "header" ? "header" : "primary" })),
    ...related.filter((p) => !primaryPids.has(p.pid)).map((p) => ({ ...p, rolle: "related" })),
  ];

  // Stufen-Umschalter: nur bei mehreren Vorwaerts-Stufen sinnvoll. Bei genau einer
  // Vorwaerts-Stufe (Ein-Stufen-Space, Ziel-Stufe = Endstufe) verschwindet der Block
  // komplett und die Ansicht ist identisch zum Verhalten vor Etappe C.
  const stufenListe = Array.isArray(forwardStages) ? forwardStages : [];
  const hatMehrereStufen = stufenListe.length > 1;
  const pidStufen = pdStagesByPid || {};
  const allStageKeys = stufenListe.map((s) => s.key);
  // Renderer fuer die Stufen-Zeile eines Pids. Der Aufrufer entscheidet, ob die Zeile
  // ueberhaupt gezeigt wird (nur wenn Pid ausgewaehlt UND mehr als eine Stufe existiert).
  const renderStufen = pid => {
    if (!selSet.has(pid)) return null;
    const active = Array.isArray(pidStufen[pid]) ? new Set(pidStufen[pid]) : new Set(allStageKeys);
    const selectedStages = stufenListe.filter(stage => active.has(stage.key));
    const mode = conflictingEdgePids.includes(pid) ? "mixed" : pdEdgeMode({ pdEdgeModeByPid, pdEdgeByPid, pdStagesByPid: { [pid]: selectedStages.map(stage => stage.key) } }, pid);
    const setMode = (partner, value, targets) => {
      if (onSetEdgeMode) onSetEdgeMode(partner, value, targets);
      else selectedStages.forEach(stage => onSetEdge(partner, stage.key, value === "edge" ? targets[stage.key] || null : null));
    };
    return <Box className="pd-review-stages" role="group" aria-label={t("s5.pdStagesAria", { pid })}>
      {devStage && devSpaceId && <Box className="pd-review-stage pd-review-dev">
        <Box className="pd-review-dev-label"><strong>DEV</strong><span>{devStage.name}</span></Box>
        <Box sx={{ minWidth: 0 }}>
          <PartnerDirectoryEdgeSync key={`${devSpaceId}:${devStage.instanceId}:${pid}`} instanceId={devStage.instanceId}
            instanceName={devStage.name} pid={pid} deferUntilOpen transportDev={{ spaceId: devSpaceId }} sharedCapabilities={devCapabilities}
            blocked={devDisabled} blockedReason="transportBusy" />
          <Box sx={{ mt: .75, color: "var(--t2)", fontSize: 13 }}>{t("pdEdge.devHint")}</Box>
        </Box>
      </Box>}
      <PdEdgeModeControl pid={pid} mode={mode} stages={selectedStages} targets={pdEdgeByPid[pid]}
        capabilities={edgeCapabilities.values} disabled={disabled} onChange={setMode}
        choiceRequired={edgeDefaultsEnabled && !pdEdgeModeByPid[pid] && selectedStages.some(stage =>
          edgeRuntimeChoiceRequired(edgeCapabilities.values[stage.instanceId], pdEdgeByPid[pid]?.[stage.key], pdEdgeChoicesByPid[pid], stage.key))} />
      {stufenListe.map(stage => <Box className="pd-review-stage" key={stage.key}>
        <FormControlLabel className="pd-review-stage-label" label={<span><strong>{stage.code || stageLabel(stage.key)}</strong><span>{stage.name}</span></span>}
          control={<Checkbox checked={active.has(stage.key)} disabled={disabled}
            inputProps={{ "aria-label": `${pid} · ${stage.code || stageLabel(stage.key)}${stage.region ? " " + stage.region : ""}` }} onChange={() => onToggleStage(pid, stage.key)} />} />
        {active.has(stage.key) && (mode === "edge" || (mode === "mixed" && pdEdgeByPid[pid]?.[stage.key]))
          ? <PdEdgeRuntime pid={pid} stage={stage} runtimeId={pdEdgeByPid[pid]?.[stage.key]}
            capabilities={edgeCapabilities.values[stage.instanceId]} disabled={disabled}
            onRetry={edgeCapabilities.reload} onChange={runtimeId => onSetEdge(pid, stage.key, runtimeId)} />
          : active.has(stage.key) && <Box sx={{ color: "var(--t2)", fontSize: 13, py: 1 }}>{t("pdEdge.cloud")}</Box>}
      </Box>)}
    </Box>;
  };

  // "Eigene hinzufuegen": alles aus pdSource MINUS automatisch erkannt (via reference
  // ODER via header - beides schon in Block 1). Systemeigene Kennungen bleiben als
  // ausgegraute Zeile.
  const custom = (pdSource.entries || []).filter((e) => !detectedPidSet.has(e.Pid));
  const q = pdSearch.trim().toLowerCase();
  const customFiltered = q ? custom.filter((e) => e.Pid.toLowerCase().includes(q)) : custom;

  // Anzahlen fuer die Kopfzeile
  const selectableCustom = custom.filter((e) => !e.systemGrund);
  const selCustomCount = selectableCustom.filter((e) => selSet.has(e.Pid)).length;
  const selAutoCount = [...detectedPidSet].filter((p) => selSet.has(p)).length;
  const totalSelected = selectedPids.length;
  const totalDetected = detectedPidSet.size;

  const setAllCustom = (on) => {
    const cur = new Set(selectedPids);
    for (const e of selectableCustom) {
      if (on) cur.add(e.Pid); else cur.delete(e.Pid);
    }
    onSetAll([...cur]);
  };
  const setAllAuto = (on) => {
    const cur = new Set(selectedPids);
    for (const p of detectedPidSet) {
      if (on) cur.add(p); else cur.delete(p);
    }
    onSetAll([...cur]);
  };

  return (
    <div className="pd-picker">
      {/* Zusammenfassungszeile */}
      <div className="pd-picker-sum">
        {totalSelected > 0
          ? t("s5.pdSelSummary", { n: totalSelected })
          : t("s5.pdSelSummaryNone")}
      </div>

      {/* Block 1: Automatisch erkannt */}
      <div className="pd-block">
        <div className="pd-block-h">
          <div>
            <div className="pd-block-title">{t("s5.pdAutoTitle")}</div>
            <div className="pd-block-sub">{t("s5.pdAutoSub")}</div>
          </div>
          <div className="pd-block-actions">
            <span className="tiny muted">{t("s5.pdSelCount", { n: selAutoCount, m: totalDetected })}</span>
            {totalDetected > 0 && (
              <>
                <Button type="button" size="small" disabled={disabled} onClick={() => setAllAuto(true)}>{t("s5.pdAll")}</Button>
                <Button type="button" size="small" disabled={disabled} onClick={() => setAllAuto(false)}>{t("s5.pdNone")}</Button>
              </>
            )}
          </div>
        </div>
        {pdAuto.loading && <LinearProgress aria-label={t("s5.pdAutoLoading")} />}
        {!pdAuto.loading && !pdAuto.error && totalDetected === 0 && <div className="pd-empty">{t("s5.pdAutoNone")}</div>}
        {!pdAuto.loading && totalDetected > 0 && (
          <div className="pd-list">
            {anzeige.map((eintrag) => {
              // Rolle bestimmt Text + optionalen Header-Kontext (Sender/Interface).
              const rolleTxt = eintrag.rolle === "related"
                ? t("s5.pdRoleRelated")
                : eintrag.rolle === "header"
                  ? t("s5.pdRoleHeader")
                  : t("s5.pdRolePrimary");
              const headerCtx = eintrag.rolle === "header" && eintrag.sender
                ? (eintrag.iface
                    ? t("s5.pdHeaderCtx", { s: eintrag.sender, i: eintrag.iface })
                    : t("s5.pdHeaderCtxShort", { s: eintrag.sender }))
                : null;
              return (
                <div key={eintrag.pid} className="pd-item-wrap">
                  <label className="pd-item">
                    <Checkbox disabled={disabled}
                      inputProps={{ "aria-label": eintrag.pid }}
                      checked={selSet.has(eintrag.pid)}
                      onChange={() => onToggle(eintrag.pid)}
                    />
                    <span className="pd-pid">{eintrag.pid}</span>
                    {headerCtx && <span className="pd-meta">{headerCtx}</span>}
                    <span className="pd-role">{rolleTxt}</span>
                  </label>
                  {renderStufen(eintrag.pid)}
                </div>
              );
            })}
          </div>
        )}
        {/* Kein Sperrgrund: Einlegen bleibt möglich, die Partner-IDs lassen sich erneut ermitteln oder unten selbst wählen. */}
        {pdAuto.error && !pdAuto.loading && (
          <Alert severity="warning" sx={{ overflowWrap: "anywhere" }}
            action={onRetryAuto && <Button color="inherit" size="small" disabled={disabled} onClick={onRetryAuto}>{t("s5.pdAutoRetry")}</Button>}>{pdAuto.error}</Alert>
        )}
        {hatMehrereStufen && totalDetected > 0 && !pdAuto.loading && (
          <div className="pd-stages-hint">{t("s5.pdStagesHint")}</div>
        )}
      </div>

      {/* Block 2: Eigene hinzufuegen */}
      <Accordion className="pd-block" expanded={customOpen} onChange={(_event, open) => onCustomOpen?.(open)}
        disableGutters elevation={0} sx={{ background: "var(--bg1)", color: "inherit", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreRounded sx={{ color: "var(--t2)" }} />} aria-label={t("s5.pdCustomTitle")}
          sx={{ px: 0, minHeight: 44, "&.Mui-focusVisible": { outline: "2px solid var(--info)", outlineOffset: 2 } }}>
          <div>
            <div className="pd-block-title">{t("s5.pdCustomTitle")}</div>
            <div className="pd-block-sub">{t("s5.pdCustomSub")}</div>
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
        <div className="pd-block-h">
          <div className="pd-block-actions">
            <span className="tiny muted">{t("s5.pdSelCount", { n: selCustomCount, m: selectableCustom.length })}</span>
            {selectableCustom.length > 0 && (
              <>
                <Button type="button" size="small" disabled={disabled} onClick={() => setAllCustom(true)}>{t("s5.pdAll")}</Button>
                <Button type="button" size="small" disabled={disabled} onClick={() => setAllCustom(false)}>{t("s5.pdNone")}</Button>
              </>
            )}
          </div>
        </div>
        {pdSource.loading && <LinearProgress aria-label={t("s5.pdCustomLoading")} />}
        {!pdSource.loading && !pdSource.error && (pdSource.entries || []).length === 0 && (
          <div className="pd-empty">{t("s5.pdCustomEmpty")}</div>
        )}
        {!pdSource.loading && custom.length > 0 && (
          <>
            <TextField fullWidth size="small" disabled={disabled}
              label={t("s5.pdCustomSearch")}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded /></InputAdornment> }}
              value={pdSearch}
              onChange={(e) => setPdSearch(e.target.value)}
            />
            <div className="pd-list pd-list--scroll">
              {customFiltered.map((entry) => {
                const isSystem = !!entry.systemGrund;
                const meta = t("s5.pdCountsMeta", {
                  s: (entry.stringParams || []).length,
                  b: (entry.binaryParams || []).length,
                  a: (entry.altPartners || []).length,
                });
                return (
                  <div key={entry.Pid} className="pd-item-wrap">
                    <label className={"pd-item" + (isSystem ? " pd-item--disabled" : "")}>
                      <Checkbox
                        inputProps={{ "aria-label": entry.Pid }}
                        disabled={disabled || isSystem}
                        checked={!isSystem && selSet.has(entry.Pid)}
                        onChange={() => !isSystem && onToggle(entry.Pid)}
                      />
                      <span className="pd-pid">{entry.Pid}</span>
                      <span className="pd-meta">{meta}</span>
                      {isSystem && <span className="pd-sys">{t("s5.pdSystem")}</span>}
                    </label>
                    {!isSystem && renderStufen(entry.Pid)}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {pdSource.error && !pdSource.loading && (
          <Alert severity="warning" sx={{ overflowWrap: "anywhere" }}
            action={onRetryCustom && <Button color="inherit" size="small" disabled={disabled} onClick={onRetryCustom}>{t("s5.pdCustomRetry")}</Button>}>{pdSource.error}</Alert>
        )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
